import {
  IconCheck,
  IconCornerDownRight,
  IconSelector,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import {
  Button,
  ListBox,
  ListBoxItem,
  Select,
  type Key,
} from "react-aria-components";
import { Size } from "../helpers/helpers.js";
import { Label } from "../Label/label.js";
import { menuStyle } from "../Menu/menuStyle.js";
import { SimpleMenuPopover } from "../Menu/simpleMenuPopover.js";
import { selectStyle } from "./style.js";

/** String value used to represent the null/empty option. */
const nullKey = "_null";
const valueToKey = (value: Key | null) => (value === null ? nullKey : value);
const keyToValue = (key: Key) => (key === nullKey ? null : key);

export interface SimpleTreeSelectProps<T extends Key | null> {
  leftAccessory?: ReactNode;
  label?: string;
  isLabelHidden?: boolean;
  isDisabled?: boolean;
  placeholder?: ReactNode;
  options: readonly SimpleTreeSelectOption<T>[];
  disabledValues?: readonly T[];
  value: T | null;
  onChange: (value: T) => void;
}

export interface SimpleTreeSelectOption<T extends Key | null> {
  value: T;
  icon?: ReactNode;
  label: string;
  children?: readonly SimpleTreeSelectOption<T>[];
}

interface FlattenedOption<T extends Key | null> {
  value: T;
  icon?: ReactNode;
  label: string;
  depth: number;
}

/**
 * Flattens a tree of options into a flat array with depth information.
 */
const flattenOptions = <T extends Key | null>(
  options: readonly SimpleTreeSelectOption<T>[],
  depth = 0,
): FlattenedOption<T>[] => {
  const result: FlattenedOption<T>[] = [];
  for (const option of options) {
    result.push({
      value: option.value,
      icon: option.icon,
      label: option.label,
      depth,
    });
    if (option.children) {
      result.push(...flattenOptions(option.children, depth + 1));
    }
  }
  return result;
};

export const SimpleTreeSelect = <T extends Key | null>(
  props: SimpleTreeSelectProps<T>,
) => {
  const flattenedOptions = flattenOptions(props.options);
  const selectedOption = flattenedOptions.find(
    (option) => option.value === props.value,
  );

  return (
    <Select
      className={
        !props.isLabelHidden && props.label ? "min-w-0 space-y-2" : "contents"
      }
      isDisabled={props.isDisabled}
      disabledKeys={props.disabledValues?.map(valueToKey)}
      value={valueToKey(props.value)}
      onChange={(key) => {
        const value = key === null ? null : keyToValue(key);
        const option = flattenedOptions.find(
          (option) => option.value === value,
        );
        if (!option) {
          throw new Error("Selected key not found in options");
        }

        props.onChange(option.value);
      }}
      aria-label={props.isLabelHidden ? props.label : undefined}
    >
      {!props.isLabelHidden && props.label ? (
        <Label size={Size.SMALL}>{props.label}</Label>
      ) : null}
      <Button className={selectStyle().button()}>
        {props.leftAccessory ? (
          <div className="shrink-0 text-gray-700">{props.leftAccessory}</div>
        ) : null}
        {selectedOption &&
        (selectedOption.value !== null || !props.placeholder) ? (
          <>
            {selectedOption.icon ? (
              <div className="shrink-0 text-gray-700">
                {selectedOption.icon}
              </div>
            ) : null}
            <div className={selectStyle().value()}>{selectedOption.label}</div>
          </>
        ) : (
          <div className={selectStyle().placeholder()}>{props.placeholder}</div>
        )}
        <IconSelector className={selectStyle().chevron()} />
      </Button>
      <SimpleMenuPopover>
        <ListBox
          items={flattenedOptions}
          disallowEmptySelection={flattenedOptions.every(
            (option) => option.value !== null,
          )}
        >
          {(option) => (
            <ListBoxItem
              id={valueToKey(option.value)}
              textValue={option.label}
              className={menuStyle().item({ className: "justify-start" })}
            >
              {option.depth > 0 ? (
                <IconCornerDownRight
                  className="size-4 shrink-0 text-gray-400"
                  style={{
                    marginLeft: `${2 + Math.max(0, option.depth - 1) * 24}px`,
                    marginRight: "-2px",
                  }}
                />
              ) : null}
              {option.icon ? (
                <div className="shrink-0">{option.icon}</div>
              ) : null}
              <span className="flex-1 truncate">{option.label}</span>
              <IconCheck
                className={menuStyle().itemIcon({
                  className: "invisible text-indigo-600 group-selected:visible",
                })}
              />
            </ListBoxItem>
          )}
        </ListBox>
      </SimpleMenuPopover>
    </Select>
  );
};
